import type { ColumnDef } from "@tanstack/react-table";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import type { FilaProveedorCotizado } from "../types/cotizacionProveedores.types";
import { IconArrowBadgeDownFilled, IconArrowBadgeUpFilled, IconMail, IconPhone } from "@tabler/icons-react";

const COLOR_SI = "#15803D";
const COLOR_NO = "#DC2626";

const ANCHO_EXPANDIR = 48;
const ANCHO_PROVEEDOR = 150;
const ANCHO_TOTAL = 130;
const ANCHO_RESPONDIO = 100;

export const ANCHO_MINIMO_PROVEEDORES =
    ANCHO_EXPANDIR + ANCHO_PROVEEDOR + ANCHO_TOTAL + ANCHO_RESPONDIO;

export interface TextosProveedores {
    proveedor: string;
    contacto: string;
    correo: string;
    rfc: string;
    telefono: string;
    productos: string;
    total: string;
    respondio: string;
    si: string;
    no: string;
    verProductos: string;
}

export const construirColumnasProveedores = (
    textos: TextosProveedores
): ColumnDef<FilaProveedorCotizado, unknown>[] => [
        {
            accessorKey: "nombreComercial",
            header: textos.proveedor,
            meta: { ancho: ANCHO_PROVEEDOR },
            cell: ({ row }) => (
                <Box>
                    <Typography variant="h6" fontWeight={500} sx={{ p: 0 }}>
                        {row.original.nombreComercial || "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12, display: "block" }}>
                        {row.original.razonSocial}
                    </Typography>
                    <Typography variant="caption" fontWeight={500} color="text.secondary" sx={{ fontSize: 12, display: "block", color: "#5c32ab" }}>
                        {row.original.rfc}
                    </Typography>
                    <Typography variant="caption" fontWeight={500} color="text.secondary" sx={{ fontSize: 12, display: "block", color: "#c49216" }}>
                        <IconMail stroke={2} size={16} /> {row.original.correo} <b style={{ color: "#9c4358" }}><IconPhone stroke={2} size={16} /> {row.original.telefono}</b>
                    </Typography>
                </Box>
            ),
        },
        {
            accessorKey: "total",
            header: textos.total,
            meta: { ancho: ANCHO_TOTAL, alineacion: "right" },
            cell: ({ row }) =>
                <Typography variant="caption" sx={{ fontSize: 14, display: "block" }}>
                    {row.original.total.toLocaleString("es-MX", { style: "currency", currency: "MXN" })}
                </Typography>
        },
        {
            accessorKey: "respondido",
            header: textos.respondio,
            meta: { ancho: ANCHO_RESPONDIO, alineacion: "center" },
            cell: ({ row }) => {
                const color = row.original.respondido ? COLOR_SI : COLOR_NO;
                return (
                    <Chip
                        label={row.original.respondido ? textos.si : textos.no}
                        size="small"
                        sx={{
                            color,
                            backgroundColor: `${color}1A`,
                            fontWeight: 600,
                            border: `1px solid ${color}`,
                        }}
                    />
                );
            },
        },
        {
            id: "expandir",
            header: "",
            enableSorting: false,
            meta: { ancho: ANCHO_EXPANDIR, alineacion: "center" },
            cell: ({ row }) =>
                row.getCanExpand() ? (
                    <IconButton
                        size="small"
                        aria-label={textos.verProductos}
                        onClick={row.getToggleExpandedHandler()}
                    >
                        {row.getIsExpanded() ? <IconArrowBadgeUpFilled /> : <IconArrowBadgeDownFilled />}
                    </IconButton>
                ) : null,
        },
    ];